import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { CATEGORY_META, CATEGORY_ORDER, FILTER_TAGS } from "@/lib/i18n";
import { ArrowLeft, Save, Plus, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title_en: "", title_hi: "", category: "jobs", organization: "",
  advertisement_number: "", total_vacancies: "",
  application_start_date: "", application_last_date: "", exam_date: "",
  application_fee: "", age_limit: "", educational_qualification: "",
  vacancy_details: [], category_wise_vacancy: [], selection_process: [],
  salary: "", how_to_apply: "", important_links: [],
  notification_pdf_url: "", official_website_url: "", apply_online_url: "",
  faqs: [], featured_image_url: "",
  seo_title: "", seo_description: "", keywords: [],
  tags: [], state: "",
  is_featured: false, is_published: true, scheduled_at: "",
  slug: "",
};

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </label>
  );
}

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-sm text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none bg-white";
const areaCls = "w-full min-h-[120px] p-3 border border-slate-300 rounded-sm text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none bg-white";

export default function AdminPostForm() {
  const { user, loading } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!id;

  useEffect(() => {
    if (!isEdit || !user) return;
    api.get(`/admin/posts/${id}`).then((r) => {
      const d = r.data;
      setForm({
        ...EMPTY,
        ...d,
        vacancy_details: d.vacancy_details || [],
        category_wise_vacancy: d.category_wise_vacancy || [],
        selection_process: d.selection_process || [],
        important_links: d.important_links || [],
        faqs: d.faqs || [],
        keywords: d.keywords || [],
        tags: d.tags || [],
        scheduled_at: d.scheduled_at || "",
      });
    });
  }, [id, isEdit, user]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setArrRow = (k, i, v) => setForm((f) => ({ ...f, [k]: f[k].map((r, j) => (j === i ? v : r)) }));
  const addArr = (k, v) => setForm((f) => ({ ...f, [k]: [...f[k], v] }));
  const rmArr = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const body = { ...form };
      if (!body.scheduled_at) body.scheduled_at = null;
      if (isEdit) await api.put(`/admin/posts/${id}`, body);
      else await api.post(`/admin/posts`, body);
      toast.success(isEdit ? "Post updated" : "Post created");
      nav("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-post-form">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 text-sm" data-testid="back-to-dashboard">
              <ArrowLeft size={14} /> Back
            </Link>
            <Logo size="sm" />
          </div>
          <button
            data-testid="admin-save-btn"
            form="post-form"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-4 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-70"
          >
            <Save size={14} /> {saving ? "Saving…" : (isEdit ? "Update Post" : "Create Post")}
          </button>
        </div>
      </header>

      <form id="post-form" onSubmit={submit} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <h1 className="font-display text-2xl font-bold text-slate-900">
          {isEdit ? "Edit Post" : "New Post"}
        </h1>

        {error && (
          <div className="p-3 border border-red-200 bg-red-50 text-red-800 text-sm rounded-md" data-testid="admin-form-error">
            {error}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-5 grid gap-4 sm:grid-cols-2">
          <Field label="Title (English) *">
            <input data-testid="f-title-en" required value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Title (Hindi)">
            <input data-testid="f-title-hi" value={form.title_hi} onChange={(e) => set("title_hi", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Category *">
            <select data-testid="f-category" required value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_META[c].en}</option>)}
            </select>
          </Field>
          <Field label="Organization">
            <input data-testid="f-org" value={form.organization} onChange={(e) => set("organization", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Advertisement No.">
            <input value={form.advertisement_number} onChange={(e) => set("advertisement_number", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Total Vacancies">
            <input value={form.total_vacancies} onChange={(e) => set("total_vacancies", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Application Start"><input value={form.application_start_date} onChange={(e) => set("application_start_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Application Last Date"><input value={form.application_last_date} onChange={(e) => set("application_last_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Exam Date"><input value={form.exam_date} onChange={(e) => set("exam_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Application Fee"><input value={form.application_fee} onChange={(e) => set("application_fee", e.target.value)} className={inputCls} /></Field>
          <Field label="Age Limit"><input value={form.age_limit} onChange={(e) => set("age_limit", e.target.value)} className={inputCls} /></Field>
          <Field label="Salary / Pay Scale"><input value={form.salary} onChange={(e) => set("salary", e.target.value)} className={inputCls} /></Field>
          <Field label="Educational Qualification">
            <input value={form.educational_qualification} onChange={(e) => set("educational_qualification", e.target.value)} className={inputCls} />
          </Field>
          <Field label="State"><input value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></Field>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <Field label="How to Apply">
            <textarea data-testid="f-how-to-apply" value={form.how_to_apply} onChange={(e) => set("how_to_apply", e.target.value)} className={areaCls} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Apply Online URL"><input value={form.apply_online_url} onChange={(e) => set("apply_online_url", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
            <Field label="Notification PDF URL"><input value={form.notification_pdf_url} onChange={(e) => set("notification_pdf_url", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
            <Field label="Official Website URL"><input value={form.official_website_url} onChange={(e) => set("official_website_url", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
            <Field label="Featured Image URL"><input value={form.featured_image_url} onChange={(e) => set("featured_image_url", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
          </div>
        </div>

        {/* Vacancy details */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">Vacancy Rows</div>
            <button type="button" data-testid="add-vacancy" onClick={() => addArr("vacancy_details", { name: "", count: "" })} className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-2">
            {form.vacancy_details.map((v, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2">
                <input value={v.name || ""} onChange={(e) => setArrRow("vacancy_details", i, { ...v, name: e.target.value })} placeholder="Post name" className={inputCls} />
                <input value={v.count || ""} onChange={(e) => setArrRow("vacancy_details", i, { ...v, count: e.target.value })} placeholder="Count" className={inputCls} />
                <button type="button" onClick={() => rmArr("vacancy_details", i)} className="h-10 border border-red-200 text-red-600 rounded-sm hover:bg-red-50 grid place-items-center"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">FAQs</div>
            <button type="button" data-testid="add-faq" onClick={() => addArr("faqs", { question: "", answer: "" })} className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-3">
            {form.faqs.map((f, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">FAQ #{i + 1}</span>
                  <button type="button" onClick={() => rmArr("faqs", i)} className="p-1 border border-red-200 text-red-600 rounded-sm hover:bg-red-50"><X size={12} /></button>
                </div>
                <input value={f.question} onChange={(e) => setArrRow("faqs", i, { ...f, question: e.target.value })} placeholder="Question" className={inputCls} />
                <textarea value={f.answer} onChange={(e) => setArrRow("faqs", i, { ...f, answer: e.target.value })} placeholder="Answer" className={areaCls} />
              </div>
            ))}
          </div>
        </div>

        {/* Important Links */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">Important Links</div>
            <button type="button" onClick={() => addArr("important_links", { label: "", url: "" })} className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-2">
            {form.important_links.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_2fr_36px] gap-2">
                <input value={l.label || ""} onChange={(e) => setArrRow("important_links", i, { ...l, label: e.target.value })} placeholder="Label" className={inputCls} />
                <input value={l.url || ""} onChange={(e) => setArrRow("important_links", i, { ...l, url: e.target.value })} placeholder="https://…" className={inputCls} />
                <button type="button" onClick={() => rmArr("important_links", i)} className="h-10 border border-red-200 text-red-600 rounded-sm hover:bg-red-50 grid place-items-center"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Selection Process */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">Selection Process (one per line)</div>
          </div>
          <textarea
            value={form.selection_process.join("\n")}
            onChange={(e) => set("selection_process", e.target.value.split("\n").map((s) => s).filter((s) => s.trim() !== ""))}
            className={areaCls}
            placeholder="Tier-I Exam&#10;Tier-II Exam&#10;Interview"
          />
        </div>

        {/* Tags */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600 mb-3">Filter Tags (used for search & filter)</div>
          <div className="flex flex-wrap gap-1.5">
            {FILTER_TAGS.map((t) => {
              const on = form.tags.includes(t.slug);
              return (
                <button
                  key={t.slug}
                  type="button"
                  data-testid={`tag-${t.slug}`}
                  onClick={() => toggleTag(t.slug)}
                  className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}
                >{t.en}</button>
              );
            })}
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white border border-slate-200 rounded-md p-5 grid gap-4">
          <Field label="SEO Title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} /></Field>
          <Field label="SEO Description"><textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={areaCls} /></Field>
          <Field label="Keywords (comma separated)">
            <input value={form.keywords.join(", ")} onChange={(e) => set("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} />
          </Field>
          <Field label="Slug (optional)" hint="Auto-generated from title if left blank">
            <input value={form.slug} onChange={(e) => set("slug", e.target.value)} className={inputCls} />
          </Field>
        </div>

        {/* Publish controls */}
        <div className="bg-white border border-slate-200 rounded-md p-5 flex flex-wrap gap-6 items-center">
          <label className="inline-flex items-center gap-2 text-sm">
            <input data-testid="f-published" type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} />
            <span>Published</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input data-testid="f-featured" type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
            <span>Featured</span>
          </label>
          <Field label="Schedule (ISO datetime, optional)">
            <input value={form.scheduled_at || ""} onChange={(e) => set("scheduled_at", e.target.value)} className={inputCls} placeholder="2026-03-01T09:00:00Z" />
          </Field>
        </div>
      </form>
    </div>
  );
}
